import React from 'react';
import { X, FileText, Download, Eye, Clock } from 'lucide-react';
import { formatDateTime } from '../../utils/helpers';
import Button from '../ui/Button';
import { motion, AnimatePresence } from 'framer-motion';

const VersionHistoryModal = ({ isOpen, onClose, history, onView, attachmentName }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        >
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]"
          >
            
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-50 p-2 rounded-lg text-indigo-600">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Version History</h3>
                  <p className="text-sm text-gray-500">
                    {attachmentName}
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-4 space-y-3 bg-gray-50/50 flex-1">
              {history.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  No history found
                </div>
              ) : (
                history.map((ver, index) => (
                  <div 
                    key={ver.id} 
                    className={`flex items-center justify-between p-4 bg-white rounded-lg border ${index === 0 ? 'border-indigo-200 shadow-sm' : 'border-gray-200'} hover:border-indigo-300 transition-all`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${index === 0 ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                        <span className="font-bold text-sm">v{ver.versionNumber}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {index === 0 ? 'Current Version' : `Version ${ver.versionNumber}`}
                          </span>
                          {index === 0 && (
                            <span className="px-2 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded-full">
                              LATEST
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                          <span>{formatDateTime(ver.createdAt)}</span>
                          <span>•</span>
                          <span>by {ver.uploadedByName || 'Unknown'}</span>
                          <span>•</span>
                          <span>{(ver.fileSize / 1024).toFixed(1)} KB</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onView(ver)}
                        className="text-gray-600 hover:text-indigo-600 hover:bg-indigo-50"
                      >
                        <Eye className="h-4 w-4 mr-1.5" />
                        View
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VersionHistoryModal;
